import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { get, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

import callIcon from '../../../assets/icons/call.svg';
import emailIcon from '../../../assets/icons/email.svg';
import addressIcon from '../../../assets/icons/location.svg';
import saveBigIcon from '../../../assets/icons/save.svg';
import nameIcon from '../../../assets/icons/user-square.svg';
import vendorIcon from '../../../assets/icons/vendor.svg';
import { useModal } from '../../../hooks/useModal';
import { useNotification } from '../../../hooks/useNotification';
import { needRefreshState } from '../../../recoil/needRefresh';
import { GET_GEOCODING_SUGGESTIONS } from '../../../services/graphql/address/address-querys';
import { CREATE_VENDOR } from '../../../services/graphql/vendors/vendorsMutation';
import { EMAIL_REGEX, PHONE_REGEX } from '../../../utils/RegexValidation';
import Breadcrumb from '../../ui/common/Breadcrumb';
import { Button } from '../../ui/common/Button';
import InputField from '../../ui/common/Inputs/InputField';

type Inputs = {
  name: string;
  phone: string;
  address: string;
  email: string;
  owner_name: string;
  owner_phone: string;
  owner_email: string;
};

const NewVendor = () => {
  const { t } = useTranslation('common');

  const {
    notifySaveSuccess,
    notifyVendorExistsError,
    notifySaveError,
    notifyAddressError,
  } = useNotification();
  // Form

  const [addressFocused, setaddressFocused] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      name: '',
      phone: '',
      address: '',
      email: '',
      owner_name: '',
      owner_phone: '',
      owner_email: '',
    },
  });

  const address = watch('address');

  // Change data for refresh tables
  const setNeedRefresh = useSetRecoilState(needRefreshState);

  const refreshOrder = () => {
    setNeedRefresh(true);
  };

  const endRefreshOrder = () => {
    setTimeout(() => {
      setNeedRefresh(false);
    }, 500);
  };

  // On submit success close modal notification
  const { handleCloseAllModals } = useModal();

  const closeOrder = () => {
    handleCloseAllModals();
    refreshOrder();
    endRefreshOrder();
  };

  // Query
  const [createVendors, { loading: createVendorLoading }] = useMutation<any>(
    CREATE_VENDOR,
    {
      onCompleted: (res) => {
        if (res.createVendor.code !== 200) {
          if (
            res.createVendor.data.includes(
              'Vendor phone number already exists.',
            )
          ) {
            notifyVendorExistsError();
          } else {
            notifySaveError();
          }
        } else {
          notifySaveSuccess();
          closeOrder();
          reset();
        }
      },
      onError: () => {
        notifySaveError();
      },
    },
  );

  // Geocoding

  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const [showAddressList, setShowAddressList] = useState<boolean>(false);

  const { data: fetchAddressData } = useQuery<any>(GET_GEOCODING_SUGGESTIONS, {
    variables: {
      address: address || '',
    },
    skip: !address,
  });

  useEffect(() => {
    if (
      fetchAddressData &&
      fetchAddressData.getAddressSuggestions.code === 200
    ) {
      if (addressFocused) {
        setShowAddressList(true);
      }

      setAddressSuggestions(
        fetchAddressData.getAddressSuggestions.data.map(
          (item: any) => item.place,
        ),
      );
    }
  }, [fetchAddressData]);

  useEffect(() => {
    if (!addressFocused) {
      // We give time to hit the selected option
      setTimeout(() => {
        setShowAddressList(false);
      }, 200);
    }
    if (addressFocused && addressSuggestions.length > 0) {
      setShowAddressList(true);
    }
  }, [addressFocused]);

  const handleAddressSelection = (selectedOption: string) => {
    setSelectedAddress(selectedOption);
    setValue('address', selectedOption);
    setShowAddressList(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputAddress = e.target.value;

    setValue('address', inputAddress);
    setSelectedAddress(inputAddress);

    if (inputAddress.trim() === '') {
      setShowAddressList(false);
    }
  };

  const validateAddress = (_selectedAddress: string) => {
    if (_selectedAddress === '') return true;
    return addressSuggestions.includes(_selectedAddress);
  };

  // On Submit
  const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
    if (createVendorLoading) return;

    const isValid = validateAddress(selectedAddress);
    if (isValid) {
      createVendors({
        variables: {
          ...data,
        },
      });
    } else {
      notifyAddressError();
    }
  };

  return (
    <>
      <Breadcrumb
        title={`${t('vendors.title-h1')} > ${t('modalNewVendor.newVendor')}`}
        image={vendorIcon}
        id="VendorModal"
      />
      <h1 className="mt-6 text-xl font-[600]">
        {t('modalNewVendor.newVendor')}
      </h1>
      <form className="my-6">
        {/* Name Input */}
        <InputField
          label={t('modalNewVendor.vendorName')}
          selectedValue={watch('name')}
          doNotUseSaveSelfButton
          type="text"
          icon={nameIcon}
          errors={errors.name}
          register={register}
          registerName="name"
          validationSchema={{
            required: t('global.required'),
          }}
        />

        {/* Phone Input */}
        <InputField
          label={t('modalNewVendor.phone')}
          selectedValue={watch('phone')}
          doNotUseSaveSelfButton
          type="text"
          icon={callIcon}
          errors={errors.phone}
          register={register}
          registerName="phone"
          validationSchema={{
            required: t('global.required'),
            pattern: {
              value: PHONE_REGEX,
              message: t('global.phone-error'),
            },
          }}
        />

        {/* Address Input */}
        <div className="my-2 h-[60px]">
          <label htmlFor="address" className="block text-[14px] font-[600]">
            {t('modalNewVendor.address')}
          </label>
          <div
            className={`relative flex w-full border-x-0 border-b-2 border-t-0 border-b-graydark            
            ${
              get(errors, 'address') &&
              'border-x-0 border-b-2 border-t-0 border-b-red'
            }`}
          >
            <img src={addressIcon} alt="address icon" />

            <input
              {...register('address', {
                validate: validateAddress,
              })}
              autoComplete="off"
              onChange={handleInputChange}
              onFocus={() => setaddressFocused(true)}
              onBlur={() => setaddressFocused(false)}
              type="text"
              id="address"
              className={`block w-full border-none  p-1.5 text-sm font-medium placeholder:text-[#5A5A5A] focus:outline-none focus:ring-0`}
              placeholder={t('modalNewVendor.address')}
            />
            {errors.address?.type === 'validate' && (
              <p
                className="absolute right-0 top-[-19px] text-[12px] font-bold text-redish"
                role="alert"
              >
                {t('modalNewVendor.address-error')}
              </p>
            )}
            {showAddressList && (
              <div className="geocoding-datalist absolute top-10 z-30 flex max-h-[160px] w-full flex-col overflow-y-auto border-[1px] border-graylight bg-white">
                {addressSuggestions.map((option, index) => (
                  <div
                    className="flex h-full w-full cursor-pointer border-b-[1px] border-graydark p-1 text-[12px] font-medium hover:bg-graydark"
                    key={index}
                    onClick={() => handleAddressSelection(option)}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Email Input */}
        <InputField
          label={t('modalNewVendor.email')}
          selectedValue={watch('email')}
          doNotUseSaveSelfButton
          type="text"
          icon={emailIcon}
          errors={errors.email}
          register={register}
          registerName="email"
          validationSchema={{
            pattern: {
              value: EMAIL_REGEX,
              message: t('global.email-error'),
            },
          }}
        />

        {/* Owner Name Input */}
        <InputField
          label={t('modalNewVendor.vendorOwnerName')}
          selectedValue={watch('owner_name')}
          doNotUseSaveSelfButton
          type="text"
          icon={nameIcon}
          errors={errors.owner_name}
          register={register}
          registerName="owner_name"
          validationSchema={{
            required: t('global.required'),
          }}
        />

        {/* Owner Phone Input */}
        <InputField
          label={t('modalNewVendor.ownerPhone')}
          selectedValue={watch('owner_phone')}
          doNotUseSaveSelfButton
          type="text"
          icon={callIcon}
          errors={errors.owner_phone}
          register={register}
          registerName="owner_phone"
          validationSchema={{
            pattern: {
              value: PHONE_REGEX,
              message: t('global.phone-error'),
            },
          }}
        />

        {/* Owner Email Input */}
        <InputField
          label={t('modalNewVendor.ownerEmail')}
          selectedValue={watch('owner_email')}
          doNotUseSaveSelfButton
          type="text"
          icon={emailIcon}
          errors={errors.owner_email}
          register={register}
          registerName="owner_email"
          validationSchema={{
            pattern: {
              value: EMAIL_REGEX,
              message: t('global.email-error'),
            },
          }}
        />

        {/* Save Button */}
        <div className="mt-2 flex justify-end">
          <Button
            text={t('modalNewVendor.save')}
            variant="green"
            icon={saveBigIcon}
            disabled={createVendorLoading}
            loading={createVendorLoading}
            onClick={handleSubmit(onSubmit)}
            type="button"
            id="save-new-vendor"
          />
        </div>
      </form>
    </>
  );
};

export default NewVendor;
